import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, BookOpen, Plus, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useCurrentUser } from "@/hooks/use-current-user";
import {
  useCoachKeyResults,
  type CoachError,
  type CoachMessage,
} from "@/hooks/use-coach";
import { CoachFeedbackCard } from "./coach-feedback-card";
import { KrEntryCard } from "./kr-entry-card";
import { ReflectionDialog } from "./reflection-dialog";
import {
  getKrTitleLines,
  logCoachSignals,
  resolveFamiliarityOverride,
  useCommitOnStreamEnd,
  useGraduation,
} from "./coach-helpers";
import {
  MAX_KRS,
  createEmptyKr,
  type WizardKrDraft,
  type WizardState,
} from "./wizard-state";

interface KrStepProps {
  state: WizardState;
  onChange: (next: WizardState) => void;
  onBackToObjective: () => void;
  onCommit: () => Promise<void> | void;
  isSaving: boolean;
}

export function KrStep({
  state,
  onChange,
  onBackToObjective,
  onCommit,
  isSaving,
}: KrStepProps) {
  const { keyResults, objective, setup, krs } = state;
  const { currentUser } = useCurrentUser();
  const statement = objective.statement.trim();

  const {
    feedback: holisticFeedback,
    isStreaming: holisticStreaming,
    error: holisticError,
    signals: holisticSignals,
    analyze: analyzeHolistic,
    reset: resetHolistic,
  } = useCoachKeyResults();

  const holisticRound = useHolisticRound(
    holisticStreaming,
    holisticError,
    holisticFeedback,
  );

  const [returnBannerDismissed, setReturnBannerDismissed] = useState(false);
  const { reflectionOpen, triggerGraduation, finishReflection, skipReflection } =
    useGraduation({
      reflectionSubmitted: krs.reflectionSubmitted,
      markReflectionSubmitted: () =>
        onChange({ ...state, krs: { ...krs, reflectionSubmitted: true } }),
      onAdvance: onCommit,
    });

  const updateKr = (localId: string, next: WizardKrDraft) => {
    onChange({
      ...state,
      keyResults: keyResults.map((k) => (k.localId === localId ? next : k)),
    });
  };

  const removeKr = (localId: string) => {
    onChange({
      ...state,
      keyResults: keyResults.filter((k) => k.localId !== localId),
    });
  };

  const addKr = () => {
    if (keyResults.length >= MAX_KRS) return;
    const q =
      setup.quarter === "annual"
        ? (keyResults[keyResults.length - 1]?.quarter ?? "Q1")
        : setup.quarter;
    onChange({ ...state, keyResults: [...keyResults, createEmptyKr(q)] });
  };

  const canCoachHolistic =
    !holisticStreaming &&
    statement.length > 0 &&
    keyResults.some((k) => k.title.trim().length > 0);

  const requestHolistic = useCallback(async () => {
    if (!canCoachHolistic) return;

    const titles = getKrTitleLines(keyResults);

    // Capture first-draft snapshot of the KR set on first coaching call.
    if (!krs.firstDraft) {
      onChange({ ...state, krs: { ...krs, firstDraft: titles } });
    }

    const overrideFamiliarity = resolveFamiliarityOverride(
      setup.familiarity,
      currentUser?.familiarity,
    );

    await analyzeHolistic({
      objective_statement: statement,
      key_results: keyResults
        .filter((k) => k.title.trim().length > 0)
        .map((k) => ({
          title: k.title.trim(),
          type: k.type,
          target_value:
            k.type === "metric" && k.targetValue ? Number(k.targetValue) : null,
        })),
      mode: "holistic",
      prior_feedback: krs.history,
      ...(setup.parentObjectiveId
        ? { parent_objective_id: setup.parentObjectiveId }
        : {}),
      ...(overrideFamiliarity ? { familiarity: overrideFamiliarity } : {}),
    });
  }, [
    analyzeHolistic,
    canCoachHolistic,
    currentUser?.familiarity,
    keyResults,
    krs,
    onChange,
    setup.familiarity,
    setup.parentObjectiveId,
    state,
    statement,
  ]);

  useCommitOnStreamEnd(
    holisticStreaming,
    holisticError,
    holisticFeedback,
    () => {
      const titles = getKrTitleLines(keyResults);
      const nextHistory: CoachMessage[] = [
        ...krs.history,
        { role: "user", content: titles },
        { role: "assistant", content: holisticFeedback },
      ];
      onChange({ ...state, krs: { ...krs, history: nextHistory } });
      logCoachSignals("kr_set", holisticSignals);
      if (holisticSignals.returnToObjective) setReturnBannerDismissed(false);
    },
  );

  const holisticRetry = () => {
    resetHolistic();
    void requestHolistic();
  };

  const finalDraftText = getKrTitleLines(keyResults);

  return (
    <div className="space-y-6">
      <ObjectiveSummary
        statement={objective.statement}
        level={setup.level ?? ""}
        type={setup.type ?? ""}
      />

      <ReturnToObjectiveBanner
        signal={holisticSignals.returnToObjective}
        dismissed={returnBannerDismissed}
        onDismiss={() => setReturnBannerDismissed(true)}
        onBack={onBackToObjective}
      />

      <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] p-4 text-sm text-[hsl(var(--muted-foreground))]">
        <p>
          A strong Key Result is{" "}
          <span className="font-medium text-foreground">specific and time-bound</span>,{" "}
          <span className="font-medium text-foreground">aggressive yet realistic</span>, and{" "}
          <span className="font-medium text-foreground">measurable and verifiable</span>. Ask
          yourself: <em>Could someone verify this was achieved without watching me work?</em> If
          not, it&rsquo;s a task, not a result.
        </p>
        <p className="mt-2">
          When you&rsquo;re done, check: <em>If I achieve all of these at 100%, am I certain I&rsquo;ve achieved the objective?</em> Aim for 3&ndash;5 Key Results per Objective (2 minimum).
        </p>
      </div>

      <div className="space-y-4">
        {keyResults.map((kr, i) => (
          <KrEntryCard
            key={kr.localId}
            index={i}
            kr={kr}
            objectiveStatement={objective.statement}
            onChange={(next) => updateKr(kr.localId, next)}
            onRemove={() => removeKr(kr.localId)}
            canRemove={keyResults.length > 1}
          />
        ))}

        <Button
          type="button"
          variant="outline"
          onClick={addKr}
          disabled={keyResults.length >= MAX_KRS}
          className="h-auto w-full border-dashed py-6 text-[hsl(var(--muted-foreground))]"
        >
          <Plus className="mr-2 h-5 w-5" />
          Add Key Result
          {keyResults.length >= MAX_KRS && (
            <span className="ml-2 text-xs">(max {MAX_KRS})</span>
          )}
        </Button>
      </div>

      <Card className="bg-[hsl(var(--muted))]">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Get feedback on how your Key Results work together as a set.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={requestHolistic}
            disabled={!canCoachHolistic}
          >
            <Sparkles className="mr-2 h-3.5 w-3.5" />
            Coach All KRs
          </Button>
        </CardContent>
      </Card>

      <KrHolisticFeedback
        feedback={holisticFeedback}
        roundNumber={holisticRound}
        isStreaming={holisticStreaming}
        error={holisticError}
        onRetry={holisticRetry}
      />

      <KrReadyCta
        readiness={holisticSignals.readiness}
        isStreaming={holisticStreaming}
        isSaving={isSaving}
        onCommit={triggerGraduation}
      />

      <ReflectionDialog
        open={reflectionOpen}
        entityType="kr_set"
        firstDraft={krs.firstDraft || finalDraftText}
        finalDraft={finalDraftText}
        onSubmitted={finishReflection}
        onSkip={skipReflection}
      />
    </div>
  );
}

interface ObjectiveSummaryProps {
  statement: string;
  level: string;
  type: string;
}

function ObjectiveSummary({ statement, level, type }: ObjectiveSummaryProps) {
  return (
    <Card className="border-l-4 border-l-[hsl(var(--accent))]">
      <CardContent className="space-y-2 p-5">
        <div className="flex flex-wrap gap-2">
          {level && (
            <span className="rounded-full bg-[hsl(var(--primary))] px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--primary-foreground))]">
              {level}
            </span>
          )}
          {type && (
            <span className="rounded-full border border-[hsl(var(--border))] px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
              {type}
            </span>
          )}
        </div>
        <p className="text-base">{statement || <em className="text-[hsl(var(--muted-foreground))]">No objective statement</em>}</p>
      </CardContent>
    </Card>
  );
}

function useHolisticRound(
  isStreaming: boolean,
  error: CoachError | null,
  feedback: string,
): number {
  const round = useRef(0);
  const prev = useRef(isStreaming);

  useEffect(() => {
    if (!prev.current && isStreaming) {
      round.current += 1;
    }
    prev.current = isStreaming;
  }, [isStreaming]);

  useEffect(() => {
    if (!isStreaming && !error && feedback.length === 0) {
      round.current = 0;
    }
  }, [isStreaming, error, feedback]);

  return round.current || (isStreaming ? 1 : 0);
}

export function isKrStepReady(state: WizardState): boolean {
  return state.keyResults.length >= 2;
}

function ReturnToObjectiveBanner({ signal, dismissed, onDismiss, onBack }: {
  signal: boolean;
  dismissed: boolean;
  onDismiss: () => void;
  onBack: () => void;
}) {
  if (!signal || dismissed) return null;
  return (
    <div className="rounded-md border border-sky-200 bg-sky-50 p-4 text-sm">
      <p className="font-semibold text-sky-900">
        Drafting these KRs revealed your Objective may need refinement.
      </p>
      <p className="mt-1 text-sky-800">
        Return to Objective coaching to sharpen the target, then come back.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Objective coaching
        </Button>
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </div>
  );
}

function KrHolisticFeedback({ feedback, roundNumber, isStreaming, error, onRetry }: {
  feedback: string;
  roundNumber: number;
  isStreaming: boolean;
  error: CoachError | null;
  onRetry: () => void;
}) {
  if (!isStreaming && feedback.length === 0 && !error) return null;
  return (
    <CoachFeedbackCard
      feedback={feedback}
      roundNumber={roundNumber}
      isStreaming={isStreaming}
      error={error}
      onRetry={error && error.kind !== "aborted" ? onRetry : undefined}
    />
  );
}

function KrReadyCta({ readiness, isStreaming, isSaving, onCommit }: {
  readiness: string | null | undefined;
  isStreaming: boolean;
  isSaving: boolean;
  onCommit: () => void;
}) {
  if (readiness !== "ready" || isStreaming) return null;
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm">
      <p className="font-semibold text-emerald-900">Your Key Results look ready.</p>
      <p className="mt-1 text-emerald-800">
        Commit and save this draft. You can still edit on the detail page.
      </p>
      <Button
        size="sm"
        className="mt-2 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
        disabled={isSaving}
        onClick={onCommit}
      >
        <BookOpen className="mr-2 h-4 w-4" />
        {isSaving ? "Saving…" : "Commit & Save"}
      </Button>
    </div>
  );
}
